import { Scene } from "phaser";
import { EventBus } from "../../EventBus.ts";
import { EVENTS_NAME } from "../../consts.ts";
import { mobileAndTabletCheck } from "../../helpers/gameobject-to-object-point.ts";
import { MusicState } from "../../../App.tsx";

export class SoundTrackScene extends Scene {
  nextSong: string = "0";
  currentSong: Phaser.Sound.BaseSound;
  // Must match the React-side default (App.tsx MusicState): muted at start,
  // playback begins only when the player unmutes via the speaker icon.
  musicState: MusicState = { muted: true, volume: 0.25 };

  constructor() {
    super("SoundTrackScene");
  }

  update() {
    if (!mobileAndTabletCheck()) {
      try {
        if (
          this.currentSong &&
          !this.currentSong.isPlaying &&
          !this.musicState.muted
        ) {
          this.nextSong = ((parseInt(this.nextSong) + 1) % 5).toString();
          console.log(`starting next song ${this.nextSong}`);

          // Check if sound system is available
          if (this.sound) {
            try {
              this.currentSong = this.sound.add(this.nextSong, {
                volume: this.musicState.volume,
              });
              if (this.currentSong) {
                this.currentSong.play();
              }
            } catch (songError) {
              console.error("Error creating or playing next song:", songError);
            }
          } else {
            console.warn(
              "Sound system not available when trying to play next song in update",
            );
          }
        }
      } catch (error) {
        console.error("Error in update when handling music:", error);
      }
    }
  }

  create(): void {
    console.log("Starting sound track");
    this.initListeners();
    if (!mobileAndTabletCheck()) {
      try {
        // Check if sound system is available
        if (this.sound) {
          try {
            this.currentSong = this.sound.add(this.nextSong, { volume: 0.5 });

            // NB: deliberately NOT touching this.game.sound.mute/volume —
            // that's the GLOBAL switch and it silenced combat SFX along with
            // the music. The mute/volume here applies to the music track only.

            // Play the song if it was successfully created — but never
            // auto-start while muted; the unmute handler below starts one.
            if (this.currentSong && !this.musicState.muted) {
              this.currentSong.play();
            }
          } catch (songError) {
            console.error("Error creating or playing song:", songError);
          }
        } else {
          console.warn("Sound system not available during initialization");
        }
      } catch (error) {
        console.error("Error initializing sound system:", error);
      }

      // Listen for music state changes
      EventBus.on(EVENTS_NAME.music, (musicState?: MusicState) => {
        if (musicState) {
          // If we received a full state object, use it
          this.musicState = musicState;
        } else {
          // For backward compatibility, toggle mute state if no state was provided
          this.musicState.muted = !this.musicState.muted;
        }

        // Apply the state to the MUSIC TRACK only. The old code flipped
        // this.game.sound.mute (Phaser's global switch), which also killed
        // combat/monster SFX — those must stay audible with music off.
        try {
          if (this.currentSong) {
            const song = this.currentSong as Phaser.Sound.WebAudioSound;
            if (typeof song.setVolume === "function") {
              song.setVolume(this.musicState.volume);
            }
            if (this.musicState.muted && this.currentSong.isPlaying) {
              this.currentSong.pause();
            } else if (!this.musicState.muted && this.currentSong.isPaused) {
              this.currentSong.resume();
            }
          }
        } catch (error) {
          // console.error("Error updating music track properties:", error);
        }

        // If unmuting and no song is playing, start one
        if (
          !this.musicState.muted &&
          (!this.currentSong || !this.currentSong.isPlaying)
        ) {
          try {
            // Check if sound system is available
            if (this.sound) {
              try {
                this.currentSong = this.sound.add(this.nextSong, {
                  volume: this.musicState.volume,
                });
                if (this.currentSong) {
                  this.currentSong.play();
                }
              } catch (songError) {
                // console.error("Error creating or playing song:", songError);
              }
            } else {
              console.warn(
                "Sound system not available when trying to play song",
              );
            }
          } catch (error) {
            console.error("Error handling unmute play:", error);
          }
        }
        // console.log(`Music state updated: muted=${this.musicState.muted}, volume=${this.musicState.volume}`);
      });
    }
  }

  private initListeners(): void {}

  stop(): void {
    // Clean up event listeners when the scene is shutdown
    EventBus.removeListener(EVENTS_NAME.music);

    // Call the parent class's shutdown method
    this.scene.stop("SoundTrackScene");
  }
}
